import type { PcoRecommendations } from '@pco/api/graphQLDefinitions/queries/recommendations/pcoRecommendations.types';
import { Box } from '@pco/atoms/Box';
import { Button } from '@pco/atoms/Button';
import { Card } from '@pco/atoms/Card';
import { Link } from '@pco/atoms/Link';
import { Media } from '@pco/atoms/Media';
import { Typography } from '@pco/atoms/Typography';
import { breakpointUtils } from '@pco/brandThemes/toyota';
import { PATHS } from '@pco/constants/paths';
import type { CMSImage } from '@pco/modules/CategoriesNavigation/index.types';
import { useConfig } from '@pco/molecules/Config';
import type { CmsCTAProps } from '@pco/molecules/CTAButton';
import { ProductNumber } from '@pco/molecules/ProductCard/components/ProductNumber';
import { ProductPrice } from '@pco/molecules/ProductPrice';
import type { Nullish } from '@pco/types';
import { getProductBrand, getProductPartNumber } from '@pco/utils/product';
import styled from 'styled-components';
import { useVerticalAlignIfSingleLine } from '../hooks/useVerticalAlignIfSingleLine';
import type { Status } from '../index.types';
import { getCTALabel } from '../utils/getCTALabel';
import { getKindByStatus } from '../utils/getKindByStatus';
import { StatusIcon } from './StatusIcon';

interface SheetProductCardProps {
	productView: Nullish<PcoRecommendations.ProductView>;
	status: Status;
	actionCTA: CmsCTAProps;
	addItemHandler: (productView: PcoRecommendations.ProductView) => void;
	labels: {
		successLabel: string;
		errorLabel: string;
		pendingLabel: string;
		defaultLabel: string;
	};
	placeholderImage?: CMSImage;
}

const ImageContent = styled(Media)`
  border-radius:  ${({ theme }) => theme.semanticTokens.radius.cards};
	height: 80px;
	width: 80px;
	object-fit: cover; 
	flex-shrink: 0;
`;

const Container = styled(Card)`
  border-radius:  ${({ theme }) => theme.semanticTokens.radius.cards};
  border: 1px solid ${({ theme }) => theme.semanticTokens.colors.border.linePrimary};
`;

const LinkContainer = styled(Link)`
	text-decoration: none;	
`;

const StyledButton = styled(Button)<{
	status?: 'default' | 'pending' | 'success' | 'error';
}>`
	&:disabled {
		cursor: not-allowed;
		pointer-events: none;

		${({ theme, status }) =>
			status === 'pending' &&
			`
				border-color: ${theme.semanticTokens.colors.content.primary};
				color: ${theme.semanticTokens.colors.content.primary};
			`}
	}
`;

const IconButton = styled(Button)<{
	status?: 'default' | 'pending' | 'success' | 'error';
}>`
	&:disabled {
		cursor: not-allowed;
		pointer-events: none;

		${({ theme, status }) =>
			status === 'pending' &&
			`border-color: ${theme.semanticTokens.colors.content.primary};`}

		svg {
			color: ${({ theme, status }) => {
				switch (status) {
					case 'pending':
						return theme.semanticTokens.colors.content.primary;
					case 'success':
						return theme.semanticTokens.colors.content.state.disabledDark;
					case 'error':
						return theme.semanticTokens.colors.content.primaryInverse;
					default:
						return theme.semanticTokens.colors.content.state.disabledLight;
				}
			}};
		}
	}

	&:not(:disabled):hover svg {
		color: ${({ theme }) => theme.semanticTokens.colors.content.primaryInverse};
	}
`;

export const SheetProductCard: React.FC<SheetProductCardProps> = ({
	productView,
	status,
	actionCTA,
	addItemHandler,
	labels,
	placeholderImage,
}) => {
	const { useMobileBreakpoint } = breakpointUtils;
	const isMobile = useMobileBreakpoint({ up: false });
	const { REACT_APP_PRODUCT_DETAILS_PAGE: PDP_PATH = PATHS.PDP_PRODUCTS } =
		useConfig();
	const { alignItems, titleRef } = useVerticalAlignIfSingleLine(
		productView?.name,
	);

	if (!productView) return <div>Error getting product</div>;

	const title = productView.name;
	const sku = productView.sku;
	const brand = getProductBrand(productView.attributes);
	const partNumber = getProductPartNumber(productView.attributes);
	const href = `${PDP_PATH}/${productView.urlKey}`;

	const { msrp, currentPrice, currency } =
		productView.__typename === 'SimpleProductView'
			? {
					msrp: productView.dealer?.price?.regular?.amount?.value,
					currentPrice: productView.dealer?.price?.final?.amount?.value,
					currency: productView.price?.regular?.amount?.currency,
				}
			: {
					msrp: productView.dealer?.price?.regular?.amount?.value,
					currentPrice: productView.dealer?.price?.final?.amount?.value,
					currency: productView.dealer?.price?.regular?.amount?.currency,
				};

	const label = getCTALabel(status, labels);
	const kind = getKindByStatus(status);

	const handleClick = () => addItemHandler(productView);
	const isDisabled =
		!productView.dealer?.is_salable ||
		status === 'pending' ||
		status === 'success';

	return (
		<Container
			as={Box}
			key={sku}
			gap="12px"
			alignItems="center"
			padding="12px"
			justifyContent="space-between"
		>
			<LinkContainer href={href}>
				<Box display="flex" gap="12px" flex="1 0 0" alignItems={alignItems}>
					<ImageContent
						image={{
							desktop: {
								src: productView.images?.[0]?.url,
								alt: productView.images?.[0]?.label ?? title ?? '',
							},
							defaultErrorSrc: placeholderImage?.desktop?.src,
						}}
					/>
					<Box flexDirection="column" flex="1 0 0">
						<ProductNumber
							brand={brand}
							sku={
								productView.__typename === 'SimpleProductView'
									? (partNumber ?? '')
									: ''
							}
						/>
						<Typography _size="body16" $lineClamp={3} ref={titleRef}>
							{title}
						</Typography>
						{msrp != null && (
							<ProductPrice
								price={currentPrice}
								msrp={msrp}
								currency={currency}
								isLight={1}
							/>
						)}
					</Box>
				</Box>
			</LinkContainer>
			{isMobile ? (
				<IconButton
					status={status || 'default'}
					_size="iconMd"
					onClick={handleClick}
					disabled={isDisabled}
					_kind={kind}
					aria-label={`${label} ${title} to cart`}
					aria-live="polite"
					data-product={productView}
				>
					<StatusIcon status={status} />
				</IconButton>
			) : (
				<StyledButton
					{...actionCTA}
					_size="min"
					_kind={kind}
					onClick={handleClick}
					disabled={isDisabled}
					aria-label={`${label} ${title} to cart`}
					aria-live="polite"
					data-product={productView}
					style={{
						maxWidth: '80px',
						width: '100%',
					}}
					status={status ? status : 'default'}
				>
					{label}
				</StyledButton>
			)}
		</Container>
	);
};
